import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModelEthnicityPresets } from '@/pages/ipe/workbench/hooks/useModelEthnicityPresets';
import {
  buildModelEthnicityPresetDisplaySlots,
  filterSelectableModelEthnicityPresets,
  SYSTEM_MODEL_ETHNICITY_FEATURE,
} from '@/pages/ipe/workbench/utils/systemModelEthnicityPresetCatalog';
import { TryOnPresetCard } from './ModelTryOnPresetPicker';

/** 节点 13.1 / 14.1：手持 / 万物穿戴系统模特选择器 — 复用试衣卡片 UI；13.2 / 14.2 按 combo 过滤展示 */
export default function ModelEthnicityPresetPicker({
  featureCode = SYSTEM_MODEL_ETHNICITY_FEATURE.modelHandheld,
  modelType,
  ethnicity,
  selectedPresetId = '',
  disabled = false,
  paused = false,
  onSelect,
  presets: presetsProp,
  presetsLoading,
  presetsFallbackUsed,
}) {
  const { t } = useTranslation('workbench');
  const shouldFetch = presetsProp === undefined;
  const {
    presets: fetchedPresets,
    loading: fetchedLoading,
    fallbackUsed: fetchedFallbackUsed,
  } = useModelEthnicityPresets(
    shouldFetch ? featureCode : '',
    shouldFetch ? modelType : '',
    shouldFetch ? ethnicity : '',
  );
  const presets = shouldFetch ? fetchedPresets : presetsProp;
  const loading = shouldFetch ? fetchedLoading : Boolean(presetsLoading);
  const fallbackUsed = shouldFetch ? fetchedFallbackUsed : Boolean(presetsFallbackUsed);
  const selectorPaused = paused || disabled;

  const displaySlots = useMemo(
    () => buildModelEthnicityPresetDisplaySlots(presets, modelType, ethnicity),
    [presets, modelType, ethnicity],
  );
  const selectablePresets = useMemo(
    () => filterSelectableModelEthnicityPresets(displaySlots),
    [displaySlots],
  );

  return (
    <div
      className={`model-studio__preset-picker${selectorPaused ? ' is-paused' : ''}`}
      aria-disabled={selectorPaused || undefined}
    >
      <div className="model-studio__preset-head">
        <span className="img2img-studio__field-label model-studio__preset-title">{t('modelPresetPicker.systemModel')}</span>
        <span className="model-studio__preset-hint">
          {selectorPaused
            ? t('modelPresetPicker.pausedHint')
            : `${t('modelPresetPicker.ethnicityHint')}${fallbackUsed ? t('modelPresetPicker.staticFallbackSuffix') : ''}`}
        </span>
      </div>

      {selectorPaused ? (
        <p className="model-studio__preset-paused-note">{t('modelPresetPicker.pausedNote')}</p>
      ) : null}

      {!selectorPaused && loading ? (
        <p className="model-studio__preset-empty">{t('modelPresetPicker.loading')}</p>
      ) : null}

      {!selectorPaused && !loading && selectablePresets.length === 0 ? (
        <p className="model-studio__preset-empty">{t('modelPresetPicker.emptyEthnicity')}</p>
      ) : null}

      {!loading && displaySlots.length > 0 ? (
        <div className="model-studio__preset-grid">
          {displaySlots.map((slot) => (
            <TryOnPresetCard
              key={`${slot.id}-${slot.slotIndex}`}
              slot={slot}
              modelType={modelType}
              garmentType=""
              selectedPresetId={selectorPaused ? '' : selectedPresetId}
              disabled={selectorPaused}
              onSelect={onSelect}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
